import {
  BatchTranslationResponse,
  DetectTranslationResponse,
  TranslateOptions,
  TranslationResponse,
} from "@/types";

import { BatchTranslator, DetectLang, Translate } from "./translate";

/**
 * The Translate class allows you to interact with the translation API.
 * You can translate individual text, batch translate multiple texts,
 * and detect the language of a given text.
 */
class Translator {
  private apiKey: string;

  /**
   * @param apiKey - User's API key required for accessing the translation service
   */
  constructor(apiKey: string) {
    if (!apiKey) {
      throw new Error("API key is required to create a Translate instance.");
    }
    this.apiKey = apiKey;
  }

  /**
   * Translates a single piece of text to the target language.
   *
   * @param text - The text to translate
   * @param options - Object containing `source` and `target` languages
   * @returns A promise that resolves to the translated text or null in case of an error
   */
  async translate(
    text: string,
    { source = "auto", target = "en" }: TranslateOptions
  ): Promise<TranslationResponse | null> {
    if (!text || !target) {
      throw new Error("Both text and target language are required.");
    }

    try {
      const data = await Translate(this.apiKey, text, target, source);
      return data;
    } catch (error) {
      console.error("Translation API error:", error);
      return null;
    }
  }

  /**
   * Translates multiple texts in batch.
   *
   * @param texts - Array of texts to translate
   * @param options - Object containing `source` and `target` languages
   * @returns A promise that resolves to an array of translated texts or null in case of an error
   */
  async batchTranslate(
    texts: string[],
    { source = "auto", target = "en" }: TranslateOptions
  ): Promise<BatchTranslationResponse | null> {
    if (!texts || !target) {
      throw new Error("Both texts and target language are required.");
    }

    try {
      const data = await BatchTranslator(this.apiKey, texts, target, source);
      return data;
    } catch (error) {
      console.error("Translation API error:", error);
      return null;
    }
  }

  /**
   * Detects the language of a given text.
   *
   * @param text - The text whose language needs to be detected
   * @returns A promise that resolves to the detected language or null in case of an error
   */
  async detect(text: string): Promise<DetectTranslationResponse | null> {
    if (!text) {
      throw new Error("Text is required to detect language.");
    }

    try {
      const data = await DetectLang(this.apiKey, text);
      return data;
    } catch (error) {
      console.error("Translation API error:", error);
      return null;
    }
  }

  /**
   * Quick translation for a single piece of text.
   * This method does not require an instance of the class.
   *
   * @param text - The text to translate
   * @param apiKey - User's API key for the translation service
   * @param options - Object containing `source` and `target` languages
   * @returns The translated text or undefined if an error occurs
   */
  static async quick({
    text,
    apiKey,
    options,
  }: {
    text: string;
    apiKey: string;
    options: TranslateOptions;
  }): Promise<string | undefined> {
    try {
      const instance = new Translator(apiKey);
      const translation = await instance.translate(text, options);
      return translation?.translated_text;
    } catch (error) {
      console.error("Quick translation error:", error);
      return undefined;
    }
  }
}

export default Translator;
